import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  CircleAlertIcon,
  Clock,
  Landmark,
  LoaderCircle,
  Sparkles,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useMutation, useQuery } from "react-query";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import getAvatarFallback from "@/utils/getAvatarFallback";
import { formatDistance } from "date-fns";
import { toast } from "sonner";

export default function Invitation() {
  const navigate = useNavigate();

  const token = useParams().token;

  const {
    data: invitation,
    isLoading,
    refetch,
    status,
  } = useQuery(
    ["invitations", token],
    async () => {
      const { data } = await api.get(`/invitations/${token}`);
      return data?.data;
    },
    { enabled: true },
  );

  const { user } = useAuth();

  const location = useLocation();
  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 lg:fixed lg:inset-y-0 lg:left-0 bg-primary dark:bg-primaryDark relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 dark:opacity-[3%]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>

          {/* Main Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-card/20 px-4 py-2 rounded-md text-sm">
              <Sparkles className="w-4 h-4" />
              You're invited
            </div>

            <h1 className="text-4xl xl:text-4xl font-semibold leading-tight">
              Join your team on OneSuite
            </h1>

            <p className="text-base text-white/80 leading-relaxed max-w-md">
              Accept the invitation to access your organization's workspace and
              start collaborating in minutes.
            </p>

            {/* Feature List */}
            <div className="space-y-4 text-sm">
              {[
                "Secure email invitations",
                "Instant access after acceptance",
                "Admin-managed permissions",
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-md bg-card/20 flex items-center justify-center">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-white/90">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {["JD", "AK", "MR", "SL"].map((initials, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full bg-card/20 backdrop-blur border-2 border-white/30 flex items-center justify-center text-xs font-medium"
                >
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-sm text-white/80">
              Trusted by teams worldwide
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col bg-backgroud lg:ml-[50%]">
        {/* Header */}
        <header className="p-4 sm:p-6 flex justify-between items-center">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          <div className="flex items-center gap-3 w-full justify-end">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground hidden sm:block text-sm">
                Need a different account?
              </span>
              <Button
                onClick={() => navigate(`/login?redirect=${location.pathname}`)}
                size="sm"
                variant="outline"
                className="border-gray-300"
              >
                Sign in
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </header>

        {/* Form Content */}
        <div className="flex-1 flex items-center justify-center px-4 py-8">
          <div>
            {isLoading && (
              <Card className="w-full border border-border rounded-md">
                <div className="h-[360px] flex items-center justify-center">
                  <LoaderCircle className="mr-2 h-6 w-6 text-primary animate-spin" />
                </div>
              </Card>
            )}
            {status === "success" && (
              <>
                {invitation?.email === user?.email ? (
                  <InvitationCard
                    organizationName={invitation?.organization?.name}
                    organizationId={invitation?.organization?.id}
                    inviterName={invitation?.invitedBy?.name}
                    inviterRole={invitation?.invitedBy?.role}
                    inviterEmail={invitation?.invitedBy?.email}
                    expiresIn={formatDistance(
                      new Date(invitation?.expiresAt || new Date()),
                      new Date(),
                      {
                        addSuffix: true,
                      },
                    )}
                    isLoading={isLoading}
                    refetch={refetch}
                    token={invitation?.token}
                    accepted={invitation?.accepted}
                  />
                ) : (
                  <WrongAccountCard
                    organizationName={invitation?.organization?.name}
                    invitationEmail={invitation?.email}
                    currentUserEmail={user.email}
                    onSwitchAccount={() =>
                      navigate(
                        `/login?email=${invitation?.email}&redirect=${location.pathname}`,
                      )
                    }
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="p-4 sm:p-6 border-t border-border bg-card">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
            <p>© {year} One Suite. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="#" className="hover:text-muted-foreground">
                Terms & Conditions
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                Help
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function InvitationCard({
  organizationName,
  organizationId,
  inviterName,
  inviterRole,
  inviterEmail,
  expiresIn,
  isLoading,
  token,
  accepted,
  refetch,
}: any) {
  const navigate = useNavigate();

  const handleAccept = async () => {
    await api.post(`/invitations/${token}/accept`);
  };

  const { refetch: refetchOrg, setActiveOrg } = useOrganization();

  const acceptMutation = useMutation(handleAccept, {
    onSuccess: () => {
      toast.success("Succesfully accepted invite");
      refetch();
      setActiveOrg(organizationId);
      refetchOrg();
    },
    onError: (_: any) => {
      toast.error("Failed to accepted invite.");
    },
  });

  if (accepted) {
    return (
      <Card className="w-full border border-border rounded-md">
        <CardHeader className="space-y-2 pb-4 text-center">
          <Link to={"/"}>
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-lg font-semibold text-foreground">
            Invitation Accepted
          </CardTitle>
          <CardDescription className="leading-7 px-5 text-muted-foreground">
            You have successfully joined "{organizationName}". You can start
            working and collaborating.
          </CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-center pt-3">
          <Button
            onClick={() => {
              navigate("/choose-org");
            }}
            size="sm"
            className="w-full h-11 bg-primary hover:bg-primaryDark text-white"
          >
            Go to Dashboard
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full border border-border rounded-md">
      {isLoading ? (
        <div className="h-[400px] flex items-center justify-center">
          <LoaderCircle className="mr-2 h-6 w-6 text-primary animate-spin" />
        </div>
      ) : (
        <>
          <CardHeader className="space-y-2 pb-4 text-center">
            <div className="flex items-center justify-center">
              <img className="h-7" src="logo_iconn.png" alt="" />
            </div>

            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
              <Landmark className="size-8 text-primary" />
            </div>
            <CardTitle className="text-lg font-semibold text-foreground">
              {organizationName}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              You've been invited to join this organization
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 mt-2">
            <div className="flex items-start gap-4 p-4 bg-backgroud rounded-lg border border-border/70">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex-shrink-0 flex items-center justify-center overflow-hidden">
                <Avatar>
                  <AvatarFallback>
                    {getAvatarFallback(inviterName)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="space-y-1">
                <p className="font-medium text-sm">{inviterName}</p>
                <p className="text-sm text-muted-foreground">{inviterRole}</p>
                <p className="text-sm text-muted-foreground">{inviterEmail}</p>
              </div>
            </div>

            <Separator />

            <div className="flex items-center text-sm text-muted-foreground">
              <Clock className="mr-2 h-4 w-4" />
              <span>
                This invitation expires in{" "}
                <span className="font-medium text-muted-foreground">
                  {expiresIn}
                </span>
              </span>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col sm:flex-row gap-3">
            <Button
              size="sm"
              className="w-full h-11 bg-primary hover:bg-primaryDark text-white"
              disabled={acceptMutation.isLoading}
              onClick={() => acceptMutation.mutate()}
            >
              {acceptMutation.isLoading && (
                <LoaderCircle className="-ms-1 me-2 animate-spin" size={16} />
              )}
              Accept Invitation
            </Button>
          </CardFooter>
        </>
      )}
    </Card>
  );
}

import { LogOut, Mail } from "lucide-react";
import { useAuth } from "@/context/auth.context";
import { useOrganization } from "@/context/organization.context";

interface WrongAccountCardProps {
  organizationName: string;
  invitationEmail: string;
  currentUserEmail: string;
  onSwitchAccount: () => void;
}

function WrongAccountCard({
  organizationName,
  invitationEmail,
  currentUserEmail,
  onSwitchAccount = () => {},
}: WrongAccountCardProps) {
  return (
    <Card className="w-full border border-border rounded-md">
      <CardHeader className="space-y-2 pb-4 text-center">
        <div className="flex items-center justify-center">
          <img className="h-11" src="/logo_iconn.png" alt="" />
        </div>
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
          <Landmark className="size-6 text-primary" />
        </div>
        <CardTitle className="text-lg font-semibold text-foreground">
          {organizationName}
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Invitation to the wrong account
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 mt-2">
        <div className="rounded-md border border-red-500/50 px-4 py-3 text-red-600">
          <div className="flex gap-3">
            <CircleAlertIcon
              className="mt-0.5 shrink-0 opacity-60"
              size={16}
              aria-hidden="true"
            />
            <div className="grow space-y-1">
              <p className="text-sm font-medium">Wrong account</p>
              <ul className="list-inside list-disc text-sm opacity-80">
                <li>This invitation was sent to a different email address.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-backgroud border border-border/70 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">
              <span className="text-muted-foreground">
                Invitation sent to:{" "}
              </span>
              <span className="font-medium">{invitationEmail}</span>
            </p>
          </div>

          <div className="flex items-center gap-2">
            <LogOut className="h-4 w-4 text-muted-foreground" />
            <p className="text-sm">
              <span className="text-muted-foreground">
                You're signed in as:{" "}
              </span>
              <span className="font-medium">{currentUserEmail}</span>
            </p>
          </div>
        </div>

        <div className="text-sm text-center leading-7 text-muted-foreground">
          To accept this invitation, please sign in with the account associated
          with <span className="font-medium">{invitationEmail}</span> or request
          a new invitation to your current email.
        </div>
      </CardContent>
      <CardFooter className="flex flex-col sm:flex-row gap-3">
        <Button
          size="sm"
          className="w-full h-11 bg-primary hover:bg-primaryDark text-white"
          onClick={onSwitchAccount}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Switch Account
        </Button>
      </CardFooter>
    </Card>
  );
}
